import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "../../Components/Page";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Typography,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PercentIcon from "@mui/icons-material/Percent";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import GavelIcon from "@mui/icons-material/Gavel";
import ErrorDisplay from "../../Components/ErrorDisplay";
import Masonry from "@mui/lab/Masonry";
import moment from "moment";
import { AdminPanelSettings, Event, Security } from "@mui/icons-material";

const formatYesNo = (value) => (
  <Chip
    label={value === "YES" ? "Yes" : "No"}
    color={value === "YES" ? "success" : "error"}
    size="small"
  />
);

const formatPriorityRule = (value) => {
  try {
    const arr = JSON.parse(value);
    return arr
      .map((item) =>
        item.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      )
      .join(" → ");
  } catch {
    return value || "N/A";
  }
};

const formatValue = (value, suffix = "") => {
  if (value === null || value === undefined || value === "") return "N/A";
  return `${value}${suffix}`;
};

const ProductProfile2 = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state;

  const sectionList = [
    "Fees & Charges",
    "Repayment & Penalties",
    "Administrative Info",
    "Qualification & Limits",
    "Advanced Settings",
    "Product Dates",
  ];

  const [openSections, setOpenSections] = useState(
    sectionList.reduce((acc, title) => ({ ...acc, [title]: true }), {})
  );

  if (!product) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <ErrorDisplay
          message="No Product Data Found"
          details="Please go back to the products list and select a product to view its profile."
          actions={
            <Button variant="contained" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        />
      </Box>
    );
  }

  const toggleSection = (title) =>
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));

  const formatDate = (date) =>
    date ? moment(date).format("DD MMM YYYY, hh:mm A") : "N/A";

  const sections = [
    {
      title: "Fees & Charges",
      icon: <PaymentsIcon color="primary" />,
      color: "primary",
      fields: [
        {
          label: "Application Fee (%)",
          value: formatValue(product.application_fee_p, "%"),
        },
        {
          label: "Application Fee Amount",
          value: formatValue(
            product.application_fee_amount,
            ` ${product.currency}`
          ),
        },
        { label: "Insurance", value: formatYesNo(product.insurance_fee) },
        {
          label: "Insurance Fee (%)",
          value: formatValue(product.insurance_p, "%"),
        },
        {
          label: "Insurance Amount",
          value: formatValue(product.insurance_amount, ` ${product.currency}`),
        },
        {
          label: "Other Charges",
          value: formatValue(product.other_charges, ` ${product.currency}`),
        },
        {
          label: "Commission (%)",
          value: formatValue(product.commission_p, "%"),
        },
      ],
    },
    {
      title: "Repayment & Penalties",
      icon: <GavelIcon color="error" />,
      color: "error",
      fields: [
        {
          label: "Repayment Priority",
          value: formatPriorityRule(product.priority_rule),
        },
        {
          label: "Early Repayment",
          value: formatYesNo(product.early_repayment),
        },
        {
          label: "Early Repayment Plan",
          value: product.early_repayment_plan,
        },
        { label: "Allow Rollover", value: formatYesNo(product.allow_rollover) },
        {
          label: "Rollover Frequency",
          value: product.rollover_frequency,
        },
        {
          label: "Rollover Interest Rate",
          value: formatValue(product.rollover_interest_rate, "%"),
        },
        { label: "Rollover Times", value: product.rollover_times },
        {
          label: "Grace Period Before Rollover",
          value: formatValue(product.grace_period_b_rollover, " days"),
        },
        {
          label: "Grace Period Interest",
          value: formatValue(product.grace_period_interest, "%"),
        },
        {
          label: "Debt Collection",
          value: formatYesNo(product.debt_collection),
        },
        {
          label: "Debt Collection Type",
          value: product.debt_collection_type,
        },
        {
          label: "Debt Collection Fee",
          value: formatValue(
            product.debt_collection_fee,
            ` ${product.currency}`
          ),
        },
        {
          label: "Debt Collection Fee (%)",
          value: formatValue(product.debt_collection_fee_p, "%"),
        },
        {
          label: "Days to Debt Collection",
          value: formatValue(product.days_to_debt_collection, " days"),
        },
        { label: "Recovery Method", value: product.recovery },
        {
          label: "Installments Before Recovery",
          value: product.instalment_before_recovery,
        },
      ],
    },
    {
      title: "Qualification & Limits",
      icon: <AssignmentTurnedInIcon color="info" />,
      color: "info",
      fields: [
        {
          label: "Minimum Loan Amount",
          value: formatValue(
            product.minimum_loan_amount,
            ` ${product.currency}`
          ),
        },
        {
          label: "Maximum Loan Amount",
          value: formatValue(
            product.maximum_loan_amount,
            ` ${product.currency}`
          ),
        },
        {
          label: "Duration to Qualify",
          value: formatValue(product.duration_to_qualify, " days"),
        },
        {
          label: "Minimum Saving Amount",
          value: formatValue(
            product.minimum_saving_amount,
            ` ${product.currency}`
          ),
        },
        {
          label: "Up to Saving (%)",
          value: formatValue(product.upto_saving_percentage, "%"),
        },
        {
          label: "Maximum Saving (%)",
          value: formatValue(product.maximum_saving_percentage, "%"),
        },
        { label: "Guarantorship", value: formatYesNo(product.guarantorship) },
        {
          label: "Max Guarantor (%)",
          value: formatValue(product.maximum_guranteer_percentage, "%"),
        },
        {
          label: "Loan Collateral",
          value: formatYesNo(product.loan_colleteral),
        },
        { label: "Collateral Types", value: product.collecteral_types },
        { label: "Allow Top-up", value: formatYesNo(product.top_up) },
        {
          label: "Top-up Amount",
          value: formatValue(product.top_up_amount, ` ${product.currency}`),
        },
        { label: "Top-up (%)", value: formatValue(product.top_up_p, "%") },
      ],
    },
    {
      title: "Administrative Info",
      icon: <AdminPanelSettings color="secondary" />,
      color: "secondary",
      fields: [
        { label: "Product ID", value: product.id },
        { label: "Owner ID", value: product.product_onwer_id },
        { label: "Publisher Name", value: product.publisher_name },
        { label: "Entity ID", value: product.entity_id },
        { label: "Loan Agent", value: formatYesNo(product.loan_agent) },
        {
          label: "Agent Commission",
          value: formatValue(product.agent_commission, ` ${product.currency}`),
        },
        {
          label: "Agent Commission (%)",
          value: formatValue(product.agent_commission_p, "%"),
        },
        { label: "Notification Frequency", value: product.notification_freq },
      ],
    },
    {
      title: "Advanced Settings",
      icon: <Security color="action" />,
      color: "action.active",
      fields: [
        { label: "Loan Approval", value: product.loan_approval },
        { label: "Upfront", value: formatYesNo(product.upfront) },
        { label: "Marker", value: product.marker },
        { label: "Checker", value: product.checker },
        {
          label: "Allow Multiplier Times",
          value: formatYesNo(product.allow_multiplier_times),
        },
        { label: "Multiplier Times", value: product.multiplier_times },
      ],
    },
    {
      title: "Product Dates",
      icon: <Event color="error" />,
      color: "error",
      fields: [
        {
          label: "Publication Date",
          value: formatDate(product.publication_date),
        },
        { label: "Start Date", value: formatDate(product.start_date) },
        { label: "End Date", value: formatDate(product.end_date) },
        {
          label: "Loan Request Date",
          value: formatDate(product.loan_request_date),
        },
        {
          label: "Loan Approval Date",
          value: formatDate(product.loan_approval_date),
        },
        {
          label: "Loan Start Date",
          value: formatDate(product.loan_start_date),
        },
        { label: "Loan End Date", value: formatDate(product.loan_end_date) },
      ],
    },
  ];

  console.log(product);

  return (
    <Page
      title={"Product Profile"}
      actions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back to List
        </Button>
      }
    >
      {/* HERO CARD */}
      <Card
        sx={{
          mb: 4,
          p: 1,
          borderRadius: 3,
          background: "linear-gradient(135deg, #f3f6f9 0%, #ffffff 100%)",
          boxShadow: 2,
        }}
      >
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Product Name
              </Typography>
              <Typography variant="h5" fontWeight="medium">
                {product.loan_name}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={product.active_status}
                color={product.active_status === "active" ? "success" : "error"}
                size="small"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Product Type
              </Typography>
              <Typography variant="subtitle1">
                {product.loan_type
                  ? product.loan_type.charAt(0).toUpperCase() +
                    product.loan_type.slice(1)
                  : "Loan Product"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Currency
              </Typography>
              <Typography variant="subtitle1">{product.currency}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* SUMMARY CARDS */}
      <Grid container spacing={2} mb={2}>
        {[
          {
            label: "Loan Duration",
            value: product.loan_duration,
            icon: <AccessTimeIcon color="secondary" />,
            color: "#9c27b0",
          },
          {
            label: "Interest Type",
            value: product.interest_type,
            icon: <PercentIcon color="success" />,
            color: "#4caf50",
          },
          {
            label: "Interest Rate",
            value: `${product.loan_interest_rate}%`,
            icon: <MonetizationOnIcon color="warning" />,
            color: "#ff9800",
          },
          {
            label: "Interest Frequency",
            value: product.loan_interest_frequency,
            icon: <PaymentsIcon color="info" />,
            color: "#2196f3",
          },
          {
            label: "Installment Counts",
            value: product.instalment_counts,
            icon: <AssignmentTurnedInIcon color="secondary" />,
            color: "#6a6b86",
          },
          {
            label: "Min Installments",
            value: product.minimum_instalment_count,
            icon: <DescriptionIcon color="error" />,
            color: "#e59eaf",
          },
          {
            label: "Minimum Loan",
            value: `${
              product.currency
            } ${product.minimum_loan_amount?.toLocaleString()}`,
            icon: <AccountBalanceIcon color="info" />,
            color: "#2196f3",
          },
          {
            label: "Maximum Loan",
            value: `${
              product.currency
            } ${product.maximum_loan_amount?.toLocaleString()}`,
            icon: <MonetizationOnIcon color="success" />,
            color: "#4caf50",
          },
        ].map((item) => (
          <Grid item xs={6} sm={6} md={3} key={item.label} sx={{ mb: 2 }}>
            <Card
              sx={{
                borderBottom: `4px solid ${item.color}`,
                borderRadius: 3,
                boxShadow: `0 12px 24px -4px ${alpha(item.color, 0.2)}`,
                backgroundColor: "#fff",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    // height: "100%",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "text.secondary",
                        letterSpacing: 0.5,
                        mb: 0.5,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "text.primary" }}
                    >
                      {item.value}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: alpha(item.color, 0.2),
                      color: item.color,
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* MASONRY SECTIONS */}
      <Masonry columns={{ xs: 1, sm: 2 }} spacing={2}>
        {sections.map((section) => (
          <Card key={section.title} sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: alpha(
                        theme.palette[section.color]?.main ||
                          theme.palette.primary.main,
                        0.2
                      ),
                      color:
                        theme.palette[section.color]?.main ||
                        theme.palette.primary.main,
                    }}
                  >
                    {section.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {section.title}
                  </Typography>
                </Stack>
              }
              action={
                <IconButton onClick={() => toggleSection(section.title)}>
                  {openSections[section.title] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              }
            />
            <Collapse in={openSections[section.title]}>
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  {section.fields.map((field) => (
                    <Grid
                      item
                      key={field.label}
                      xs={field.label === "Repayment Priority" ? 12 : 6}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        {field.label}
                      </Typography>
                      <Typography variant="subtitle1">
                        {field.value || "N/A"}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Collapse>
          </Card>
        ))}
      </Masonry>
    </Page>
  );
};

export default ProductProfile2;
